import { Autocomplete, AutocompleteChangeDetails, Button, Grid, Popover, TextField, Typography } from '@mui/material';
import moment from 'moment';
import React, { MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import TonnageModel from '../Tonnage/models/TonnageModel';
import { CheckDuplicateSelector, CheckDuplicateType, setCargoData, setCheckDuplicateType, setSelectedIds, setTonnageData } from './reducers/CheckDuplicateReducer';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import './CheckDuplicate.scss';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import TonnageApi, { MergeDuplicateTonnageItemParam, MergeDuplicateTonnageParam } from '../Tonnage/api/TonnageApi';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import CargoApi, { DeleteDuplicateCargosParam } from '../Cargo/api/CargoApi';
import CargoModel from '../Cargo/models/CargoModel';

const CheckDuplicate: React.FunctionComponent = () => {
    let dispatch = useDispatch();
    let history = useHistory();

    let checkDuplicateData = useSelector(CheckDuplicateSelector);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [overviewFullTc, setOverviewFullTc] = useState<string>("");
    const [triggerClearCheckBox, setTriggerClearCheckBox] = useState<number>(0);
    const divRef = React.createRef<HTMLDivElement>();

    useEffect(() => {
        if (checkDuplicateData.checkDuplicateType === CheckDuplicateType.Tonnage && checkDuplicateData.tonnageData.length == 0) {
            refreshDuplicateTonnage();
            refreshDuplicateCargo();
        }
    }, []);

    const refreshDuplicateTonnage = () => {
        dispatch(showLoading(true));
        dispatch(setSelectedIds([]));
        setTriggerClearCheckBox(new Date().getTime());

        TonnageApi.GetDuplicateTonnages()
            .then((response: BaseResponse<TonnageModel[]>) => {
                if (response.success) {
                    dispatch(setTonnageData(response.data));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            });
    };

    const refreshDuplicateCargo = () => {
        dispatch(showLoading(true));
        dispatch(setSelectedIds([]));
        setTriggerClearCheckBox(new Date().getTime());

        CargoApi.GetDuplicateCargos()
            .then((response: BaseResponse<CargoModel[]>) => {
                if (response.success) {
                    dispatch(setCargoData(response.data));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            });
    };

    const tonnageColumns = [
        {
            Field: "vesselName",
            Title: "Vessel Name",
            AllowSort: true,
            Render: (rowData: TonnageModel) => {
                let previewButton = rowData.fullTcpreview && rowData.fullTcpreview.length > 0 ?
                    <i className="fa fa-eye" aria-hidden="true"></i> :
                    <i className="fa fa-eye-slash" aria-hidden="true"></i>;
                return <>
                    <Grid container>
                        <Grid item xs={11}>
                            {rowData.vesselName}
                        </Grid>
                        <Grid item xs={1}>
                            <Button
                                className="tonnage-table-preview-full-tc"
                                onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                                    if (rowData.fullTcpreview && rowData.fullTcpreview.length > 0) {
                                        setAnchorEl(event.currentTarget);
                                        setOverviewFullTc(rowData.fullTcpreview);
                                    }
                                }}
                                onMouseLeave={() => {
                                    setAnchorEl(null)
                                }}
                                disabled={!rowData.fullTcpreview || rowData.fullTcpreview.length === 0}
                            >
                                {previewButton}
                            </Button>
                        </Grid>
                    </Grid>
                </>
            }
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "dwt",
            Title: "DWT",
            AllowSort: false,
            Render: (rowData: TonnageModel) => {
                return <>{rowData.dwt.toLocaleString()}</>;
            }
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "yearBuilt",
            Title: "BLT",
            AllowSort: false,
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "portName",
            Title: "Port",
            AllowSort: false,
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "portArea",
            Title: "Area",
            AllowSort: false,
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "dateOpenStart",
            Title: "Date",
            AllowSort: false,
            Render: (rowData: TonnageModel) => (
                <>
                    {formatDateMMMDD(rowData.dateOpenStart).toUpperCase() + " - " + formatDateMMMDD(rowData.dateOpenEnd).toUpperCase()}
                </>
            )
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "operatorCountry",
            Title: "Country",
            AllowSort: false,
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "updatedDate",
            Title: "Update Time",
            AllowSort: false,
            Render: (rowData: TonnageModel) => (
                <>
                    {formatUpdatedDate(rowData.updatedDate)}
                </>
            )
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "userDisplayName",
            Title: "By",
            AllowSort: false,
        } as DatatableColumnModel<TonnageModel>,
        {
            Field: "operatorEmails",
            Title: "Emails",
            AllowSort: false,
            Width: "50px",
            Render: (rowData: TonnageModel) => {
                return <>
                    <Button
                        className="table-emails"
                        onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                            if (rowData.operatorEmails && rowData.operatorEmails.length > 0) {
                                setAnchorEl(event.currentTarget);
                                setOverviewFullTc(rowData.operatorEmails);
                            }
                        }}
                        onMouseLeave={() => {
                            setAnchorEl(null)
                        }}
                        disabled={!rowData.operatorEmails || rowData.operatorEmails.length === 0}
                    >
                        <i className="fa fa-envelope-o" aria-hidden="true"></i>
                    </Button>
                </>
            }
        } as DatatableColumnModel<TonnageModel>,
    ];

    const cargoColumns = [
        {
            Field: "chartererCompanyName",
            Title: "Account Name",
            AllowSort: true,
            Width: "150px",
            Render: (rowData: CargoModel) => {
                let previewButton = rowData.orderDetail && rowData.orderDetail.length > 0 ?
                    <i className="fa fa-eye" aria-hidden="true"></i> :
                    <i className="fa fa-eye-slash" aria-hidden="true"></i>;
                return <>
                    <Grid container>
                        <Grid item xs={11}>
                            {rowData.chartererCompanyName}
                        </Grid>
                        <Grid item xs={1}>
                            <Button
                                className="tonnage-table-preview-full-tc"
                                onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                                    if (rowData.orderDetail && rowData.orderDetail.length > 0) {
                                        setAnchorEl(event.currentTarget);
                                        setOverviewFullTc(rowData.orderDetail);
                                    }
                                }}
                                onMouseLeave={() => {
                                    setAnchorEl(null)
                                }}
                                disabled={!rowData.orderDetail || rowData.orderDetail.length === 0}
                            >
                                {previewButton}
                            </Button>
                        </Grid>
                    </Grid>
                </>
            }
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "dwtMin",
            Title: "Min Dwt",
            AllowSort: true,
            Render: (rowData: CargoModel) => {
                return <>{rowData.dwtMin.toLocaleString()}</>;
            }
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "dwtMax",
            Title: "Max Dwt",
            AllowSort: true,
            Render: (rowData: CargoModel) => {
                return <>{rowData.dwtMax.toLocaleString()}</>;
            }
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "loadPortName",
            Title: "Loadport",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "loadPortArea",
            Title: "Load Area",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "disportName",
            Title: "Disport",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "disportArea",
            Title: "Dis Area",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "chartererCountry",
            Title: "Country",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "lay",
            Title: "Lay Can",
            AllowSort: true,
            Render: (rowData: CargoModel) => (
                <>
                    {formatDateMMMDD(rowData.lay).toUpperCase() + " - " + formatDateMMMDD(rowData.can).toUpperCase()}
                </>
            )
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "updatedDate",
            Title: "Update Time",
            AllowSort: true,
            Render: (rowData: CargoModel) => (
                <>
                    {formatUpdatedDate(rowData.updatedDate)}
                </>
            )
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "userDisplayName",
            Title: "By",
            AllowSort: true,
        } as DatatableColumnModel<CargoModel>,
        {
            Field: "accountEmails",
            Title: "Emails",
            AllowSort: false,
            Width: "50px",
            Render: (rowData: CargoModel) => {
                return <>
                    <Button
                        className="table-emails"
                        onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                            if (rowData.accountEmails && rowData.accountEmails.length > 0) {
                                setAnchorEl(event.currentTarget);
                                setOverviewFullTc(rowData.accountEmails);
                            }
                        }}
                        onMouseLeave={() => {
                            setAnchorEl(null)
                        }}
                        disabled={!rowData.accountEmails || rowData.accountEmails.length === 0}
                    >
                        <i className="fa fa-envelope-o" aria-hidden="true"></i>
                    </Button>
                </>
            }
        } as DatatableColumnModel<CargoModel>,
    ];

    const formatDateMMMDD = (dateString: string): string => {
        let date = new Date(dateString);
        return moment(date).format("DD MMM");
    };

    const formatUpdatedDate = (dateString: string): string => {
        let date = new Date(dateString);
        return moment(date).format("DD/MM/YYYY HH:mm");
    };

    const mergeTonnages = () => {
        let mergeTonnageParam = {
            mergeItems: []
        } as MergeDuplicateTonnageParam

        let hasError = false;
        _.each(checkDuplicateData.selectedIds, (selectedId) => {
            if (hasError) {
                return;
            }

            let selectedItem = _.find(checkDuplicateData.tonnageData, (tonnage) => {
                return tonnage.id === selectedId;
            });

            if (selectedItem != undefined) {
                let duplicateItemsWithoutSelectedItem = _.filter(checkDuplicateData.tonnageData, (tonnage) => {
                    return tonnage.id !== selectedId &&
                        tonnage.vesselName?.trim() === selectedItem?.vesselName?.trim() &&
                        tonnage.dwt === selectedItem?.dwt;
                });

                if (duplicateItemsWithoutSelectedItem.length > 0) {
                    //can not select same duplicated item twice
                    //all selected id without selected id must not in duplicate item list
                    let selectedIdsWithoutSelectedId = _.filter(checkDuplicateData.selectedIds, (selectedIdToCheck) => {
                        return selectedIdToCheck !== selectedId;
                    });

                    hasError = !_.every(selectedIdsWithoutSelectedId, (selectedIdToCheck) => {
                        return _.findIndex(duplicateItemsWithoutSelectedItem, (item) => {
                            return item.id == selectedIdToCheck;
                        }) === -1;
                    })

                    if (hasError) {
                        Utility.Alert.Error("Please select only one item to keep of tonnage: " + selectedItem.vesselName);
                    } else {
                        mergeTonnageParam.mergeItems.push({
                            targetTonnageId: selectedId,
                            mergeTonnageIds: _.map(duplicateItemsWithoutSelectedItem, (item) => {
                                return item.id;
                            })
                        } as MergeDuplicateTonnageItemParam)
                    }
                }
            }
        });

        if (hasError) {
            return;
        }

        if (mergeTonnageParam.mergeItems.length === 0) {
            Utility.Alert.Error("Please select at least one item to keep");
            return;
        }

        dispatch(showLoading(true));

        TonnageApi.MergeDuplicateTonnage(mergeTonnageParam)
            .then(() => {
                Utility.Alert.Success("Merge tonnages successful");
                refreshDuplicateTonnage();
            }).catch((error) => {
                Utility.Alert.Error(error);
                dispatch(showLoading(false));
            }).finally(() => {
            })
    }

    const deleteCargos = () => {
        let deleteDuplicateCargoParam = {
            ids: []
        } as DeleteDuplicateCargosParam

        let hasError = false;
        _.each(checkDuplicateData.selectedIds, (selectedId) => {
            if (hasError) {
                return;
            }

            let selectedItem = _.find(checkDuplicateData.cargoData, (cargo) => {
                return cargo.id === selectedId;
            });

            if (selectedItem != undefined) {
                let duplicateItemsWithoutSelectedItem = _.filter(checkDuplicateData.cargoData, (cargo) => {
                    return cargo.id !== selectedId &&
                        cargo.charterer === selectedItem?.charterer &&
                        cargo.cargoType === selectedItem?.cargoType &&
                        cargo.loadPort === selectedItem?.loadPort &&
                        cargo.disport === selectedItem?.disport &&
                        cargo.lay === selectedItem?.lay &&
                        cargo.can === selectedItem?.can &&
                        cargo.dwtMin === selectedItem?.dwtMin &&
                        cargo.dwtMax === selectedItem?.dwtMax;
                });

                if (duplicateItemsWithoutSelectedItem.length > 0) {
                    //can not select same duplicated item twice
                    //all selected id without selected id must not in duplicate item list
                    let selectedIdsWithoutSelectedId = _.filter(checkDuplicateData.selectedIds, (selectedIdToCheck) => {
                        return selectedIdToCheck !== selectedId;
                    });

                    hasError = !_.every(selectedIdsWithoutSelectedId, (selectedIdToCheck) => {
                        return _.findIndex(duplicateItemsWithoutSelectedItem, (item) => {
                            return item.id == selectedIdToCheck;
                        }) === -1;
                    })

                    if (hasError) {
                        Utility.Alert.Error("Please select only one item to keep of tonnage: " + selectedItem.chartererCompanyName);
                    } else {
                        deleteDuplicateCargoParam.ids = [
                            ...deleteDuplicateCargoParam.ids,
                            ..._.map(duplicateItemsWithoutSelectedItem, (item) => {
                                return item.id
                            })
                        ];
                    }
                }
            }
        });

        if (hasError) {
            return;
        }

        if (deleteDuplicateCargoParam.ids.length === 0) {
            Utility.Alert.Error("Please select at least one item to keep");
            return;
        }

        dispatch(showLoading(true));

        CargoApi.DeleteDuplicateCargos(deleteDuplicateCargoParam)
            .then(() => {
                Utility.Alert.Success("Delete cargos successful");
                refreshDuplicateCargo();
            }).catch((error) => {
                Utility.Alert.Error(error);
                dispatch(showLoading(false));
            }).finally(() => {
            })
    }

    return <div className='datatable-wrapper'>
        <Grid
            container
            justifyContent="flex-end"
            className="table-search-result-utility-bar"
        >
            <Grid>
                <Button
                    color="primary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        if (checkDuplicateData.checkDuplicateType === CheckDuplicateType.Tonnage) {
                            mergeTonnages();
                        } else {
                            deleteCargos();
                        }
                    }}
                >Keep Selected And Delete Other</Button>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        if (checkDuplicateData.checkDuplicateType === CheckDuplicateType.Tonnage) {
                            refreshDuplicateTonnage();
                        } else {
                            refreshDuplicateCargo();
                        }
                    }}
                >Refresh</Button>
            </Grid>
            <Grid>
                <Autocomplete
                    className='table-search-result-utility-bar-item table-check-duplicate-type'
                    options={[CheckDuplicateType.Tonnage, CheckDuplicateType.Cargo]}
                    renderInput={(params) => <TextField {...params} label="Type" />}
                    value={checkDuplicateData.checkDuplicateType}
                    disableClearable={true}
                    onChange={(event: React.SyntheticEvent, value: CheckDuplicateType | null, reason: string, details?: AutocompleteChangeDetails<CheckDuplicateType> | undefined) => {
                        dispatch(setCheckDuplicateType(value ? value : CheckDuplicateType.Tonnage));
                    }}
                />
            </Grid>
        </Grid>
        {
            checkDuplicateData.checkDuplicateType === CheckDuplicateType.Tonnage &&
            <Datatable<TonnageModel>
                useCheckBox={true}
                useDefaultSearchBar={false}
                pagingType={PagingTypeEnum.None}
                data={checkDuplicateData.tonnageData}
                className="check-duplicate-search-result nice-scroll"
                useItemPerPage={false}
                columns={tonnageColumns}
                isDataLoaded={true}
                stickyHeader={true}
                onTableCheckboxSelectedChange={(selectedRows: TonnageModel[]) => {
                    let ids = _.map(selectedRows, (item) => {
                        return item.id;
                    })
                    dispatch(setSelectedIds(ids));
                }}
                triggerClearCheckBox={triggerClearCheckBox}
            ></Datatable>
        }
        {
            checkDuplicateData.checkDuplicateType === CheckDuplicateType.Cargo &&
            <Datatable<CargoModel>
                useCheckBox={true}
                useDefaultSearchBar={false}
                pagingType={PagingTypeEnum.None}
                data={checkDuplicateData.cargoData}
                className="check-duplicate-search-result nice-scroll"
                useItemPerPage={false}
                columns={cargoColumns}
                isDataLoaded={true}
                stickyHeader={true}
                onTableCheckboxSelectedChange={(selectedRows: CargoModel[]) => {
                    let ids = _.map(selectedRows, (item) => {
                        return item.id;
                    })
                    dispatch(setSelectedIds(ids));
                }}
                triggerClearCheckBox={triggerClearCheckBox}
            ></Datatable>
        }
        <Popover
            id="mouse-over-popover"
            sx={{
                pointerEvents: 'none',
            }}
            open={anchorEl != null}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            disableRestoreFocus
        >
            <Typography sx={{ p: 1 }}>{overviewFullTc}</Typography>
        </Popover>
    </div>
};

export default CheckDuplicate;